import type { SavedTargetingRules } from 'definitions';
import type { SavedTargetingSelect } from 'types';
import { useState } from 'react';
import { toast } from 'sonner';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'hooks/useAuth';
import { useClient } from 'hooks/useClient';
import { isEmptyTargeting, rulesFromExperiment, targetingHash } from 'helpers/targeting-identity';
import { apiErrorMessage } from '../api-error';
import { useExperiment } from './useExperiment';

export type SaveMode = 'new' | 'update';

type DialogState = { kind: 'save'; mode: SaveMode } | { kind: 'load'; preselectedId: number | null } | null;

/** What a load replaced, as POST /targeting/apply returns it; Undo posts it back to /targeting/restore. */
type PreviousTargeting = { rules: SavedTargetingRules; sourceId: number | null; sourceHash: string | null };

type RenameParams = { id: number; name: string };

const MENU_LIMIT = 5;
const UNDO_MS = 8000;

/**
 * Everything the Saved targeting menu and its two dialogs need for one experiment: the website's
 * entries, the experiment's current rules, where they were loaded from and whether they changed
 * since, which dialog is open, and the actions.
 *
 * Targeting saves itself rule by rule, so a load is a server call and its Undo a second one. The
 * actions toast their own result and return whether they succeeded, so a dialog can stay open on
 * a failure (a taken name, a rule the API refuses) with the API's message shown.
 */
export const useSavedTargetings = (experimentId: number) => {
  const { experiment } = useExperiment(experimentId);
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const apiClient = useClient();
  const [dialog, setDialog] = useState<DialogState>(null);

  const websiteId = experiment?.websiteId;
  const listKey = ['SAVED_TARGETINGS', websiteId];

  const { data: savedTargetings = [], isLoading } = useQuery<SavedTargetingSelect[]>({
    enabled: !!websiteId,
    queryKey: listKey,
    queryFn: () => apiClient.get<SavedTargetingSelect[]>(`/saved-targetings/website/${websiteId}`),
  });

  const currentRules = rulesFromExperiment(experiment ?? {});
  const isEmpty = isEmptyTargeting(currentRules);
  // A deleted source finds nothing and the line hides. So does an emptied Target Area: there is
  // nothing left that came from it.
  const source = isEmpty ? undefined : savedTargetings.find((entry) => entry.id === experiment?.targetingSourceId);
  const editedSinceLoad = !!source && targetingHash(currentRules) !== experiment?.targetingSourceHash;
  const isRunning = experiment?.status === 'Running';

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: listKey }),
      queryClient.invalidateQueries({ queryKey: ['EXPERIMENT', experimentId] }),
    ]);
  };

  const run = async (action: () => Promise<void>, fallback: string): Promise<boolean> => {
    try {
      await action();
      return true;
    } catch (error) {
      toast.error(apiErrorMessage(error, fallback));
      return false;
    }
  };

  const enforceNote = isRunning ? ' Press Enforce new Rules to publish it.' : '';

  const closeDialog = () => setDialog(null);
  const openSave = (mode: SaveMode) => setDialog({ kind: 'save', mode });
  const openLoad = (preselectedId: number | null = null) => setDialog({ kind: 'load', preselectedId });

  const saveNew = (name: string) =>
    run(async () => {
      await apiClient.post('/saved-targetings', { experimentId, name: name.trim() });
      await refresh();
      toast.success(`Saved "${name.trim()}"`);
      closeDialog();
    }, 'Could not save the targeting.');

  const updateExisting = (entry: SavedTargetingSelect) =>
    run(async () => {
      await apiClient.put(`/saved-targetings/${entry.id}/rules`, { experimentId });
      await refresh();
      toast.success(`Updated "${entry.name}"`);
      closeDialog();
    }, 'Could not update the saved targeting.');

  const restore = (previous: PreviousTargeting) =>
    run(async () => {
      await apiClient.post(`/experiments/${experimentId}/targeting/restore`, previous);
      await refresh();
      toast.success(`Previous targeting restored.${enforceNote}`);
    }, 'Could not undo the load.');

  const load = (entry: SavedTargetingSelect) =>
    run(async () => {
      const { previous } = await apiClient.post<{ previous: PreviousTargeting }>(
        `/experiments/${experimentId}/targeting/apply`,
        { savedTargetingId: entry.id },
      );
      await refresh();
      closeDialog();
      // One level of undo, held by this toast: a later load shows its own.
      toast.success(`Loaded "${entry.name}". Previous targeting replaced.${enforceNote}`, {
        duration: UNDO_MS,
        action: { label: 'Undo', onClick: () => void restore(previous) },
      });
    }, 'Could not load the saved targeting.');

  /** From the menu: an empty Target Area loses nothing, so it loads at once; otherwise confirm first. */
  const loadFromMenu = (entry: SavedTargetingSelect) => (isEmpty ? load(entry) : openLoad(entry.id));

  const rename = ({ id, name }: RenameParams) =>
    run(async () => {
      await apiClient.patch(`/saved-targetings/${id}`, { name: name.trim() });
      await refresh();
    }, 'Could not rename the saved targeting.');

  const remove = (id: number) =>
    run(async () => {
      await apiClient.delete(`/saved-targetings/${id}`);
      await refresh();
    }, 'Could not delete the saved targeting.');

  return {
    savedTargetings,
    recent: savedTargetings.slice(0, MENU_LIMIT),
    isLoading,
    currentRules,
    isEmpty,
    source,
    editedSinceLoad,
    isRunning,
    // The API is read-only for Members; hiding the menu spares them a 403.
    canEdit: !!user && user.role !== 'Member',
    // Activation rules are refused on a URL Redirect, so entries holding them cannot be loaded there.
    activationAvailable: experiment?.type !== 'URL Redirect',
    dialog,
    openSave,
    openLoad,
    closeDialog,
    saveNew,
    updateExisting,
    load,
    loadFromMenu,
    rename,
    remove,
  };
};

export type SavedTargetingsManager = ReturnType<typeof useSavedTargetings>;
